import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';

import { getOrParseContentBodyAdf } from '@confluence/adf-analysis-core/entry-points/getOrParseContentBodyAdf.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { useReadTimeQuery$data } from './hooks/__generated__/useReadTimeQuery.graphql.ts';
import type { ContentFeatures } from './featuresExtractor.ts';
import {
	extractContentFeatures,
	extractContentFeaturesSkipExpand,
	READ_TIME_MAX_NODES,
} from './featuresExtractor.ts';

const wpm = 265; // words per minute, inspired by Medium.com
const minute = 60;

// Displayed floor for the capped state — a "very long page" lower bound shown
// as "{floor}+ min read" when the document exceeds READ_TIME_MAX_NODES.
// Calibrated to node count: docs that actually reach ~50k nodes are structure-
// heavy data tables (~0.37 words/node from measurement), reading ~70 min at the
// cap; 60 sits just under that so the "+" stays an honest lower bound (denser
// content that reaches the cap reads more).
export const READ_TIME_CAP_FLOOR_MINUTES = 60;

export const calculateImageReadTime = (imageCount: number): number => {
	let readTime: number = 0;
	for (let i = 0; i < imageCount; i++) {
		readTime += Math.max(12 - i, 3);
	}
	return readTime;
};

// Calculate Read time based on:
// 1. Average reading speed 265 WPM
// 2. Image count (1 sec added for each image until first 10 images post that 3 sec for each subsequent image)
export const calculateReadTimeFromFeatures = (
	features: Pick<ContentFeatures, 'wordCount' | 'imageCount'>,
): number => {
	const readTime =
		((features.wordCount / wpm) * 60 + calculateImageReadTime(features.imageCount)) / minute;
	if (readTime > 0.5) {
		return Math.round(readTime);
	} else {
		return 0;
	}
};

// Decide the read-time badge value from already-extracted features and the ADF
// node count (measured during the walk). When the document exceeds the node cap
// an exact estimate is not meaningful, so we return the lower-bound floor and
// flag it capped for the "{floor}+ min read" display.
export const readTimeFromFeatures = (
	features: ContentFeatures,
	nodeCount: number,
): { readTime: number; isReadTimeCapped: boolean } => {
	if (nodeCount > READ_TIME_MAX_NODES) {
		return { readTime: READ_TIME_CAP_FLOOR_MINUTES, isReadTimeCapped: true };
	} else {
		return { readTime: calculateReadTimeFromFeatures(features), isReadTimeCapped: false };
	}
};

export const getAdfString = (response: useReadTimeQuery$data | undefined): string | null => {
	const dynamic = response?.content?.nodes?.[0]?.body?.dynamic;
	if (dynamic?.representation === 'atlas_doc_format' && dynamic?.value) {
		return dynamic.value;
	} else {
		return null;
	}
};

export const getAdf = (
	response: useReadTimeQuery$data | undefined,
	contentId?: string,
): JSONDocNode | null => {
	const representation = response?.content?.nodes?.[0]?.body?.dynamic?.representation;
	if (representation === 'atlas_doc_format') {
		const adfString = response?.content?.nodes?.[0]?.body?.dynamic?.value;
		if (adfString) {
			try {
				if (contentId && expValEquals('cc-proj-xl-use-shared-adf-parsing', 'isEnabled', true)) {
					const result = getOrParseContentBodyAdf(contentId, adfString);
					return result.ok ? result.doc : null;
				}
				return JSON.parse(adfString);
			} catch {
				return null;
			}
		}
	}
	return null;
};

export const getContentFeatureExtractedEventObjectFromFeatures = ({
	contentType,
	contentId,
	features,
	isInEditor,
}: {
	contentType: string;
	contentId: string;
	features: ContentFeatures;
	isInEditor: boolean;
}): {
	type: string;
	data: {
		source: string;
		action: string;
		actionSubject: string;
		objectType: string;
		objectId: string;
		attributes: {
			isInEditor: boolean;
			rawLength: number;
			textLength: number;
			wordCount: number;
			imageCount: number;
			inlineMacroCount: number;
			macroCount: number;
			tableCount: number;
			mediaFiles: string[];
			looms: {
				id: string;
				url: string;
			}[];
		};
	};
} => ({
	type: 'sendOperationalEvent',
	data: {
		source: 'confluence-frontend',
		action: 'extracted',
		actionSubject: 'contentFeature',
		objectType: contentType,
		objectId: contentId,
		attributes: {
			...features,
			isInEditor,
		},
	},
});

export const getContentFeatureExtractedEventObject = ({
	contentType,
	contentId,
	adf,
	isInEditor,
}: {
	contentType: string;
	contentId: string;
	adf: JSONDocNode;
	isInEditor: boolean;
}): ReturnType<typeof getContentFeatureExtractedEventObjectFromFeatures> =>
	getContentFeatureExtractedEventObjectFromFeatures({
		contentType,
		contentId,
		features: extractContentFeatures(adf),
		isInEditor,
	});

/**
 * Checks whether the given body dynamic content has any meaningful content (text or images).
 * Currently used in #cc-ce-mau experiment to determine if the page has enough content to show the "Summarize page" action (e.g. in the AI floating context menu)
 */
export const docHasContent = (
	bodyDynamic: { representation?: string | null; value?: string | null } | null | undefined,
): boolean => {
	if (!bodyDynamic || bodyDynamic.representation !== 'atlas_doc_format') {
		return false;
	}
	const adfString = bodyDynamic.value;
	if (!adfString) {
		return false;
	}
	let adf: JSONDocNode;
	try {
		adf = JSON.parse(adfString) as JSONDocNode;
	} catch {
		return false;
	}
	const { wordCount, imageCount } = extractContentFeaturesSkipExpand(adf);
	return wordCount > 0 || imageCount > 0;
};
